// Formulario.js

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import api from '../api/axiosConfig';
import { getMediaBaseUrl } from '../utils/mediaUrl';
import {
  Avatar,
  Box,
  Typography,
  Slider,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Stack,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SaveIcon from '@mui/icons-material/Save';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HistoryIcon from '@mui/icons-material/History';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CanteraStatsTab from '../components/CanteraStatsTab';
import { useStatsTheme } from './v2/theme/statsTheme';
import {
  CanteraCard,
  CanteraDialog,
  CanteraButton,
  CanteraTextField,
} from './components/shared';

const mediaUrl = getMediaBaseUrl();

const today = new Date();
const displayDate = today.toLocaleDateString('es-ES');
const dateToSend = today.toISOString().split('T')[0];

function FormularioContent({
  sleep, setSleep,
  pain, setPain,
  mood, setMood,
  nutrition, setNutrition,
  painZone, setPainZone,
  comment, setComment,
  handleOpenExplanation,
  handlePrepareSubmit,
  t,
}) {
  const theme = useStatsTheme();

  const categories = [
    {
      key: 'sleep',
      title: t('formulario.sleepTitle'),
      icon: '😴',
      description: t('formulario.sleepDescription'),
      value: sleep,
      setValue: setSleep,
      color: theme.actionColors.neutral,
    },
    {
      key: 'pain',
      title: t('formulario.painTitle'),
      icon: '💪',
      description: t('formulario.painDescription'),
      value: pain,
      setValue: setPain,
      color: theme.actionColors.missed,
      hasInput: true,
    },
    {
      key: 'mood',
      title: t('formulario.moodTitle'),
      icon: '🧠',
      description: t('formulario.moodDescription'),
      value: mood,
      setValue: setMood,
      color: theme.actionColors.warning,
    },
    {
      key: 'nutrition',
      title: t('formulario.nutritionTitle'),
      icon: '🥗',
      description: t('formulario.nutritionDescription'),
      value: nutrition,
      setValue: setNutrition,
      color: theme.actionColors.made,
    },
  ];

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Box component="form" onSubmit={handlePrepareSubmit}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            mb: 3,
          }}
        >
          {categories.map((cat) => (
            <CanteraCard key={cat.key} tone="dark" padding={2.5}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
                <Box
                  sx={{
                    fontSize: '2rem',
                    width: 54,
                    height: 54,
                    borderRadius: `${theme.radius.lg}px`,
                    bgcolor: theme.withAlpha(cat.color, 0.15),
                    border: `1px solid ${theme.withAlpha(cat.color, 0.35)}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {cat.icon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ color: cat.color, fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em' }}>
                    {cat.title}
                  </Typography>
                  <Typography sx={{ color: theme.text.secondary, fontSize: 12, fontWeight: 500 }}>
                    {cat.description}
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => handleOpenExplanation(cat.key)}
                  size="small"
                  sx={{
                    color: cat.color,
                    border: `1px solid ${theme.surfaces.border}`,
                    borderRadius: `${theme.radius.md}px`,
                    '&:hover': { bgcolor: theme.withAlpha(cat.color, 0.15) },
                  }}
                >
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Box>

              <Box sx={{ textAlign: 'center', mb: 1.5 }}>
                <Typography
                  sx={{
                    color: cat.color,
                    fontSize: 52,
                    fontWeight: 800,
                    lineHeight: 1,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {cat.value}
                </Typography>
                <Typography sx={{ color: theme.text.muted, fontSize: 11, fontWeight: 600, letterSpacing: '0.06em' }}>
                  / 10
                </Typography>
              </Box>

              <Slider
                value={cat.value}
                onChange={(_e, newVal) => cat.setValue(newVal)}
                step={1}
                marks
                min={0}
                max={10}
                valueLabelDisplay="auto"
                sx={{
                  color: cat.color,
                  '& .MuiSlider-thumb': {
                    width: 22,
                    height: 22,
                    bgcolor: cat.color,
                    border: `2px solid ${theme.surfaces.floating}`,
                    '&:hover, &.Mui-focusVisible': {
                      boxShadow: `0 0 0 8px ${theme.withAlpha(cat.color, 0.2)}`,
                    },
                  },
                  '& .MuiSlider-track': { bgcolor: cat.color, height: 6, border: 'none' },
                  '& .MuiSlider-rail': { bgcolor: theme.surfaces.border, height: 6, opacity: 1 },
                  '& .MuiSlider-mark': { bgcolor: theme.surfaces.border, height: 8, width: 2 },
                  '& .MuiSlider-markActive': { bgcolor: cat.color },
                }}
              />

              {cat.hasInput && (
                <Box sx={{ mt: 2 }}>
                  <CanteraTextField
                    label={t('formulario.painZone')}
                    fullWidth
                    size="small"
                    value={painZone}
                    onChange={(e) => setPainZone(e.target.value)}
                  />
                </Box>
              )}
            </CanteraCard>
          ))}
        </Box>

        <CanteraCard tone="dark" padding={2.5} sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
            <Box sx={{ fontSize: '1.75rem' }}>💭</Box>
            <Typography sx={{ color: theme.text.primary, fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em' }}>
              {t('formulario.generalComment')}
            </Typography>
          </Box>
          <CanteraTextField
            placeholder={t('formulario.commentPlaceholder')}
            multiline
            rows={4}
            fullWidth
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </CanteraCard>

        <Box sx={{ textAlign: 'center', pb: 2 }}>
          <CanteraButton
            type="submit"
            variant="primary"
            size="lg"
            startIcon={<SaveIcon />}
            sx={{ px: 6 }}
          >
            {t('formulario.saveTracking')}
          </CanteraButton>
        </Box>
      </Box>
    </Box>
  );
}

function Formulario() {
  const { t } = useTranslation();
  const theme = useStatsTheme();
  const { accesmail } = useParams();

  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playerNotFound, setPlayerNotFound] = useState(false);
  const [lastAverage, setLastAverage] = useState(null);

  const [sleep, setSleep] = useState(5);
  const [pain, setPain] = useState(0);
  const [mood, setMood] = useState(5);
  const [nutrition, setNutrition] = useState(5);
  const [painZone, setPainZone] = useState('');
  const [comment, setComment] = useState('');

  const [explanationDialogOpen, setExplanationDialogOpen] = useState(false);
  const [explanationDialogContent, setExplanationDialogContent] = useState('');

  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [confirmationDialogMode, setConfirmationDialogMode] = useState('confirm');
  const [confirmationDialogContent, setConfirmationDialogContent] = useState('');

  const [currentTab, setCurrentTab] = useState(0);

  const explanations = {
    sleep: t('formulario.sleepExplanation'),
    pain: t('formulario.painExplanation'),
    mood: t('formulario.moodExplanation'),
    nutrition: t('formulario.nutritionExplanation'),
  };

  const handleOpenExplanation = (key) => {
    setExplanationDialogContent(explanations[key]);
    setExplanationDialogOpen(true);
  };
  const handleCloseExplanation = () => {
    setExplanationDialogOpen(false);
    setExplanationDialogContent('');
  };

  const openConfirmationDialog = () => {
    const recap = `
${t('formulario.confirmSave')}
- ${t('formulario.sleepTitle')}: ${sleep}
- ${t('formulario.painTitle')}: ${pain} (${t('formulario.zone')}: ${painZone || t('formulario.none')})
- ${t('formulario.moodTitle')}: ${mood}
- ${t('formulario.nutritionTitle')}: ${nutrition}
- ${t('formulario.comment')}: ${comment || t('formulario.none')}
    `;
    setConfirmationDialogContent(recap);
    setConfirmationDialogMode('confirm');
    setConfirmationDialogOpen(true);
  };

  const handleConfirmationCancel = () => setConfirmationDialogOpen(false);

  const handleConfirmationConfirm = async () => {
    try {
      const formData = {
        sleep, pain, mood, nutrition, painZone, comment,
        trackDate: dateToSend,
      };
      const response = await api.post(`/api/cantera/player/${accesmail}/daily`, formData);
      setConfirmationDialogContent(response?.data?.message || t('formulario.saveSuccess'));
      setConfirmationDialogMode('result');
      fetchLastAverage(accesmail);
      setSleep(5); setPain(0); setMood(5); setNutrition(5);
      setPainZone(''); setComment('');
    } catch (error) {
      console.error('Erreur lors de l\'envoi du formulaire:', error);
      setConfirmationDialogContent(t('formulario.saveError'));
      setConfirmationDialogMode('result');
    }
  };

  const handleConfirmationDialogClose = () => setConfirmationDialogOpen(false);

  const getPlayerAvatarUrl = (playerId) =>
    playerId ? `${mediaUrl}/photos/${playerId}.png?${new Date().getTime()}` : '/path/to/default-player-avatar.png';

  useEffect(() => {
    if (accesmail) {
      fetchPlayerData(accesmail);
      fetchLastAverage(accesmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accesmail]);

  const fetchPlayerData = async (accesmailValue) => {
    try {
      setLoading(true);
      setPlayerNotFound(false);
      const response = await api.get(`/api/cantera/player/${accesmailValue}`);
      setPlayer(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors de la récupération du joueur:', error);
      if (error.response && error.response.status === 404) setPlayerNotFound(true);
      setLoading(false);
    }
  };

  const fetchLastAverage = async (accesmailValue) => {
    try {
      const response = await api.get(`/api/cantera/player/${accesmailValue}/last-average`);
      setLastAverage(response.data.lastAverage);
    } catch (error) {
      console.error('Erreur lors de la récupération du dernier estado promedio:', error);
    }
  };

  const handlePrepareSubmit = (e) => {
    e.preventDefault();
    openConfirmationDialog();
  };

  const playerName = player ? player.nom : t('formulario.unknownPlayer');
  const playerAvatar = player ? getPlayerAvatarUrl(player.id) : '';
  const statsVisible = player && player.stats_visible === 1;

  const pageBg = `radial-gradient(circle at 20% 0%, ${theme.accentSoft} 0%, transparent 45%), radial-gradient(circle at 80% 100%, ${theme.withAlpha(theme.accentSecondary, 0.12)} 0%, transparent 45%), ${theme.surfaces.base}`;

  if (playerNotFound) {
    return (
      <Box sx={{ minHeight: '100vh', background: pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Box sx={{ maxWidth: 520, width: '100%' }}>
          <CanteraCard tone="dark" padding={4}>
            <Stack spacing={2} alignItems="center" textAlign="center">
              <Box sx={{
                width: 72, height: 72, borderRadius: '50%',
                bgcolor: theme.withAlpha(theme.actionColors.missed, 0.15),
                border: `1px solid ${theme.withAlpha(theme.actionColors.missed, 0.4)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <WarningAmberIcon sx={{ color: theme.actionColors.missed, fontSize: 40 }} />
              </Box>
              <Typography sx={{ color: theme.actionColors.missed, fontSize: 24, fontWeight: 700 }}>
                {t('formulario.invalidAccess')}
              </Typography>
              <Typography sx={{ color: theme.text.secondary, fontSize: 14 }}>
                {t('formulario.invalidAccessMessage')}
              </Typography>
              <Typography sx={{ color: theme.text.muted, fontSize: 12 }}>
                {t('formulario.verifyLink')}
              </Typography>
            </Stack>
          </CanteraCard>
        </Box>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', background: pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        <CircularProgress size={28} sx={{ color: theme.accent }} />
        <Typography sx={{ color: theme.text.secondary, fontSize: 15, fontWeight: 500 }}>
          {t('formulario.loading')}
        </Typography>
      </Box>
    );
  }

  const showStatsTab = statsVisible && currentTab === 1;

  return (
    <Box sx={{ minHeight: '100vh', background: pageBg }}>
      {/* Header sticky avec joueur */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: theme.surfaces.elevated,
          borderBottom: `1px solid ${theme.surfaces.border}`,
          backdropFilter: 'blur(8px)',
          px: { xs: 2, md: 3 },
          py: 1.25,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box
              sx={{
                width: 44, height: 44,
                borderRadius: `${theme.radius.md}px`,
                bgcolor: theme.surfaces.base,
                border: `1px solid ${theme.surfaces.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                p: 0.75, flexShrink: 0,
              }}
            >
              <img src="/assets/valenciaapp.png" alt="Valencia App Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{
                color: theme.text.primary,
                fontSize: { xs: 16, md: 18 },
                fontWeight: 700,
                letterSpacing: '-0.01em',
                lineHeight: 1.2,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {playerName}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                <CalendarMonthIcon sx={{ fontSize: 13, color: theme.text.muted }} />
                <Typography sx={{ color: theme.text.secondary, fontSize: 11, fontWeight: 500 }}>
                  {t('formulario.today')}: {displayDate}
                </Typography>
              </Stack>
              {lastAverage && (
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
                  <HistoryIcon sx={{ fontSize: 13, color: theme.accent }} />
                  <Typography sx={{ color: theme.accent, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {t('formulario.lastRecord')}: {(() => {
                      let dateStr = lastAverage.track_date;
                      if (!dateStr) return '-';
                      if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}/)) {
                        const [year, month, day] = dateStr.split('T')[0].split('-');
                        return `${day}/${month}/${year}`;
                      }
                      const d = new Date(dateStr);
                      if (isNaN(d.getTime())) return '-';
                      return d.toLocaleDateString('es-ES');
                    })()} → 😴{lastAverage.sleep} 💪{lastAverage.pain} 🧠{lastAverage.mood} 🥗{lastAverage.nutrition}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Box>

          <Avatar
            src={playerAvatar}
            alt={playerName}
            sx={{
              width: { xs: 48, md: 56 },
              height: { xs: 48, md: 56 },
              border: `2px solid ${theme.accent}`,
              boxShadow: `0 0 0 3px ${theme.accentSoft}`,
              flexShrink: 0,
            }}
          />
        </Box>
      </Box>

      {/* Tabs (si stats visibles) */}
      {statsVisible && (
        <Box sx={{ borderBottom: `1px solid ${theme.surfaces.border}`, bgcolor: theme.surfaces.elevated, px: { xs: 1, md: 3 } }}>
          <Tabs
            value={currentTab}
            onChange={(_, newValue) => setCurrentTab(newValue)}
            TabIndicatorProps={{ sx: { bgcolor: theme.accent, height: 3, borderRadius: '3px 3px 0 0' } }}
            sx={{
              minHeight: 44,
              '& .MuiTab-root': {
                color: theme.text.secondary,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: 14,
                minHeight: 44,
                '&.Mui-selected': { color: theme.accent },
              },
            }}
          >
            <Tab label={t('formulario.dailyTracking')} />
            <Tab label={t('formulario.statistics')} />
          </Tabs>
        </Box>
      )}

      {/* Contenu */}
      <Box sx={{ px: showStatsTab ? 0 : { xs: 2, md: 4 }, py: showStatsTab ? 0 : 3 }}>
        {showStatsTab ? (
          player && (
            <Box sx={{ bgcolor: theme.surfaces.base, minHeight: 'calc(100vh - 110px)' }}>
              <CanteraStatsTab playerId={player.id} />
            </Box>
          )
        ) : (
          <FormularioContent
            sleep={sleep} setSleep={setSleep}
            pain={pain} setPain={setPain}
            mood={mood} setMood={setMood}
            nutrition={nutrition} setNutrition={setNutrition}
            painZone={painZone} setPainZone={setPainZone}
            comment={comment} setComment={setComment}
            handleOpenExplanation={handleOpenExplanation}
            handlePrepareSubmit={handlePrepareSubmit}
            t={t}
          />
        )}
      </Box>

      {/* Dialog explication */}
      <CanteraDialog
        open={explanationDialogOpen}
        onClose={handleCloseExplanation}
        title={t('formulario.information')}
        actions={
          <CanteraButton variant="primary" onClick={handleCloseExplanation} autoFocus>
            {t('formulario.close')}
          </CanteraButton>
        }
      >
        <Typography sx={{ whiteSpace: 'pre-line', fontSize: 15, color: theme.text.primary, lineHeight: 1.55 }}>
          {explanationDialogContent}
        </Typography>
      </CanteraDialog>

      {/* Dialog confirmation / resultat */}
      <CanteraDialog
        open={confirmationDialogOpen}
        onClose={confirmationDialogMode === 'result' ? handleConfirmationDialogClose : handleConfirmationCancel}
        title={confirmationDialogMode === 'confirm' ? t('formulario.confirmSubmit') : t('formulario.result')}
        actions={
          confirmationDialogMode === 'confirm' ? (
            <>
              <CanteraButton variant="ghost" onClick={handleConfirmationCancel}>
                {t('formulario.cancel')}
              </CanteraButton>
              <CanteraButton variant="primary" onClick={handleConfirmationConfirm}>
                {t('formulario.confirm')}
              </CanteraButton>
            </>
          ) : (
            <CanteraButton variant="primary" onClick={handleConfirmationDialogClose} autoFocus>
              {t('formulario.close')}
            </CanteraButton>
          )
        }
      >
        <Typography sx={{ whiteSpace: 'pre-line', fontSize: 15, color: theme.text.primary, lineHeight: 1.55 }}>
          {confirmationDialogContent}
        </Typography>
      </CanteraDialog>
    </Box>
  );
}

export default Formulario;
